import { EmployeeLocation, LastCertificationAtHubStatusType, Location, PaginationFilterType, Uuid } from '@/api/types';
import { DonorType, OrganType } from '@/api/apis/case';
import { AirportType, Hub, LogisticProvider, TXCenter as TXCenterType } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { TMDXFile } from '@/api/apis/files';
import {
  CCResourceType,
  CCShiftType,
  CSHolidayDutyGroup,
  CSShiftTimeGroup,
  CSShiftType,
  ShiftType,
  SShiftType,
  Unavailability,
} from './schedule';
import {
  components,
  InventoryPartType,
  LocationPreference,
  Status as EmployeeStatus,
  EmployeeSearchOrderBy,
  // OcsConsoleSearchOrderBy,
  InventoryPartsSearchOrderBy,
  Size as ScrubSize,
  AffiliationType,
  DriverAffiliationType,
  DriversSearchOrderBy,
  OcsConsoleStatus,
  TrialProgramsFilterType,
  OMSShiftType,
} from './schema.d';
import { NonNullableFields, OverrideType } from '@/types/utils';
import { TrialProgram } from '../facilities/trial-programs';

export {
  Size as ScrubSize,
  LocationPreference,
  ResourceStatus,
  NodeType as ReservationType,
  Status as EmployeeStatus,
  AffiliationType,
  EmployeeSearchOrderBy,
  OcsConsoleSearchOrderBy,
  InventoryPartsSearchOrderBy,
  DriversSearchOrderBy,
  DriverAffiliationType,
  OcsConsoleStatus,
  CandidateMatchType,
  CaseMatchType,
  ActionType as AssignActionType,
  TrialProgramsFilterType,
} from './schema.d';

export enum EmployeeType {
  all = 'all',
  employee = 'employee',
  contractor = 'contractor',
}

export enum EmployeeRole {
  // Tech
  TechSupportAdmin = 'tech-support-admin',

  Admin = 'admin',
  SuperAdmin = 'super-admin',
  CLevel = 'c-level',
  QuoteApprovers = 'quote-approver',
  // Logistic Case Management
  SrLogisticsManager = 'senior-logistics-manager',
  LogisticsManager = 'logistics-manager',
  AviationSpecialist = 'aviation-specialist',
  GroundSpecialist = 'ground-specialist',
  OrganLeadership = 'organ-leadership',
  OrganManagement = 'organ-management',
  ClinicalLead = 'clinical-lead',
  LogisticsTeamManagement = 'logistics-team-management',
  OrganManagementSpecialist = 'organ-management-specialist',

  // Clinical Resources
  ClinicalCoordinator = 'clinical-coordinator',
  ClinicalSpecialist = 'clinical-specialist',
  RetrievalSurgeon = 'retrieval-surgeon',
  ClinicalCoordinatorManagement = 'clinical-coordinator-management',
  ContractorRetrievalSurgeon = 'contractor-retrieval-surgeon',
  ClinicalAuditor = 'clinical-auditor',
  ClinicalAffairsLeadership = 'clinical-affairs-leadership',

  // Schedule
  ScheduleManagerCs = 'schedule-manager-cs',
  ScheduleManagerRs = 'schedule-manager-rs',
  ScheduleManagerCc = 'schedule-manager-cc',
  ScheduleManagerOms = 'schedule-manager-oms',

  // Other
  CommercialAdoption = 'commercial-adoption',
  CommercialAdoptionManagement = 'commercial-adoption-management',
  RegularUser = 'regular-user',
  FinanceAuditor = 'finance-auditor',
  MaintenanceOfficer = 'maintenance-officer',
  Section16Officer = 'section-16-officer',
  NationalCSManagement = 'national-cs-management',
  RegionalCSManagement = 'regional-cs-management',
}

export enum PerfusionSetOrganType {
  LiverPerfusionSet = InventoryPartType.LiverPerfusionSet,
  HeartPerfusionSet = InventoryPartType.HeartPerfusionSet,
  LungPerfusionSet = InventoryPartType.LungPerfusionSet,
  KidneyPerfusionSet = InventoryPartType.KidneyPerfusionSet,
}

// FIXME: Remove client-only fields
export type ReservationData = OverrideType<
  components['schemas']['ReservationData'],
  {
    caseName?: string;
    donorId?: string;
    organType?: OrganType;
  }
>;

export type CurrentDeploymentData = components['schemas']['CurrentDeploymentData'];

export type AffiliationData = OverrideType<
  components['schemas']['AffiliationData'],
  {
    txCenterId: string;
    opoId: string;
  }
>;

export type ContractorCertificationData = OverrideType<
  components['schemas']['ContractorCertificationData'],
  {
    organType: OrganType;
  }
>;

export type TrackedLocation = components['schemas']['TrackedLocationModel'];

export type EmployeeRestPeriod = components['schemas']['EmployeeRestPeriodModel'];

export type DeploymentData = OverrideType<
  components['schemas']['DeploymentData'],
  {
    organType: OrganType;
    donorType: DonorType;
  }
>;

export type CompositeShiftType<S = ShiftType> = {
  resourceId: string;
  region?: string; // Client-only field
  regionId: string | null;
  hubId: string | null;
  fromOffset: string;
  toOffset: string;
  shiftType: S;
  isManager: boolean | null;
  fromUtc: string;
  toUtc: string;
};

export type ShiftAvailabilityData<S = ShiftType> = {
  currentShift: CompositeShiftType<S> | null;
  isPartialCurrentShift: boolean;
  nextShift: CompositeShiftType<S> | null;
  isPartialNextShift: boolean;
  activeShifts: CompositeShiftType<S>[];
  unavailabilities: CompositeShiftType<S>[] | null;
  currentHubs?: Hub[]; // Client-only field
};

// FIXME: Remove client-only fields
export type Employee = OverrideType<
  components['schemas']['EmployeeData'],
  {
    id: Uuid;
    userId: Uuid;
    email: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    hireDate: string;
    profilePhoto?: string;
    weight: number | null;
    scrubSizeTop: ScrubSize | null;
    scrubSizeBottom: ScrubSize | null;
    tsaPrecheckNumber: string | null;
    isContractor: boolean;
    organSpecializations: OrganType[];
    phoneSupportOrganSpecializations: OrganType[];
    rsScheduleManagerOrgans: OrganType[];
    organManagementSpecialistSpecializations: OrganType[];
    proctorOrganSpecializations: OrganType[];
    shadowOrganSpecializations: OrganType[];
    trialPrograms: TrialProgram[] | null;
    phone: string;
    businessPhone: string;
    currentLocation: EmployeeLocation; // FIXME: Differ from API schema type
    homeLocation: string;
    homeLocationObject?: EmployeeLocation; // Client-only field
    baseLocation: string;
    baseLocationObject?: Hub; // Client-only field
    trackedLocation?: TrackedLocation;
    closestAirportForDeployment: number;
    roles: EmployeeRole[];
    position: string;
    isActive: boolean;
    locationPreference: LocationPreference;
    reservations: ReservationData[];
    canWorkOnExtraShifts: boolean;
    extraShiftsCount: number;
    canTravel: boolean;
    travelShiftsCount: number;
    liverSurgeonRegionId: string | null;
    heartLungsSurgeonRegionId: string | null;
    currentDeploymentData: CurrentDeploymentData;
    affiliation?: AffiliationData;
    affiliationTxCenter?: TXCenterType; // Client-only field
    affiliationOpo?: OpoOrganization; // Client-only field
    contractorCertifications: ContractorCertificationData[] | null;
    clinicalCoordinatorType: CCResourceType | null;
    clinicalSpecialistShiftGroupType: CSHolidayDutyGroup | null;
    shiftTimeGroup: CSShiftTimeGroup | null;
    deploymentDatas: DeploymentData[] | null;
    activeRestPeriod: EmployeeRestPeriod | null;
  }
>;

export type EmployeeAvailabilityData = Employee & {
  csShiftAvailabilityData?: ShiftAvailabilityData<CSShiftType>;
  sShiftAvailabilityData?: ShiftAvailabilityData<SShiftType>;
  ccShiftAvailabilityData?: ShiftAvailabilityData<CCShiftType>;
};

export type EmployeeComposed = Omit<
  EmployeeAvailabilityData,
  'baseLocation' | 'homeLocation' | 'closestAirportForDeployment'
> & {
  homeLocation?: EmployeeLocation;
  baseLocation?: Hub | null;
  closestAirportForDeployment?: AirportType;
  organType?: OrganType[];
};

export type OcsConsoleData = OverrideType<
  components['schemas']['OcsConsoleData'],
  {
    ocsConsoleName: string;
    serialNumber: string;
    caseNumber: string;
    hub?: Hub; // Client-only field
    nopHub: string;
    organType: OrganType;
    status: OcsConsoleStatus;
    logoPath?: string;
    lastInUseDaysAgo: number;
    reservations: ReservationData[];
    serialNumberPrefix: string;
    fullSerialNumber: string;
    trialProgram: TrialProgram | null;
  }
>;

export type OcsConsoleFormType = {
  ocsConsoleName: string;
  serialNumber: string;
  caseId: string | null;
  caseName?: string | null;
  nopHub: string;
  hubName?: string;
  organType: OrganType;
  status?: OcsConsoleStatus;
  isInMaintenance: boolean;
  image?: TMDXFile;
  logoPath?: string;
  trialProgramId: string | null;
};

export type PerfusionSet = OverrideType<
  components['schemas']['InventoryPartModel'],
  {
    serialCode: string;
    caseId?: string; // Client-only field
    caseNumber: string;
    type: PerfusionSetOrganType;
    trialProgram?: TrialProgram | null;
  }
>;

export type PerfusionSetListFilters = {
  inventoryPartTypes: PerfusionSetOrganType[];
};

export type PaginatedPerfusionSetListFilters = PaginationFilterType<
  PerfusionSetListFilters,
  InventoryPartsSearchOrderBy
>;

export type EmployeeListQueryFilters = {
  employeeIds: string;
  roles: string;
  organType: OrganType;
  position: string;
  baseLocation: string;
  status: EmployeeStatus;
  isContractor: boolean;
  trialProgramIds: string;
  shiftTimeGroup: string;
};

// TODO: Refactor and replace EmployeeListFilters after Api change
export type EmployeeListFilters = {
  employeeIds?: string[];
  userIds?: string[];
  organType?: OrganType;
  status?: EmployeeStatus;
  baseLocation?: string[];
  roles?: EmployeeRole[];
  position?: string[];
  isContractor?: boolean;
  surgeonAffiliationType?: AffiliationType;
  withDeleted?: boolean;
  trialProgramIds?: string[];
  shiftTimeGroup?: CSShiftTimeGroup[];
};

export type PaginatedEmployeesListFilters = PaginationFilterType<EmployeeListQueryFilters, EmployeeSearchOrderBy>;

export type OcsConsoleListFilters = {
  ids?: string[];
  organType?: OrganType[];
  nopHub?: string[];
  ocsConsoleStatus?: OcsConsoleStatus[];
  isInMaintenance?: boolean;
  trialProgramIds?: string[];
  trialProgramsFilterType?: TrialProgramsFilterType;
};

// TODO: Remove after filters approach change in Api
export type OcsConsoleListQueryFilters = {
  ids?: string;
  organType?: string;
  nopHub?: string;
  ocsConsoleStatus?: string;
  isInMaintenance?: boolean;
  trialProgramIds?: string;
  trialProgramsFilterType?: TrialProgramsFilterType;
};

// TODO: Narrow sorting type after refactoring
// export type PaginatedOcsConsoleListFilters = PaginationFilterType<OcsConsoleListQueryFilters, OcsConsoleSearchOrderBy>;
export type PaginatedOcsConsoleListFilters = PaginationFilterType<OcsConsoleListQueryFilters>;

export type AirportView = NonNullableFields<components['schemas']['AirportView']>;

export type CrewView = components['schemas']['CrewView'];

export type CrewDutyView = components['schemas']['CrewDutyView'];

export type CrewDutyAvailabilityView = OverrideType<
  components['schemas']['CrewDutyAvailabilityView'],
  {
    airport: AirportView;
    crewDutyViews: CrewDutyView[];
  }
>;

export type TripLegView = OverrideType<
  components['schemas']['TripLegView'],
  {
    arrivalAirport: AirportView;
  }
>;

export type TripView = OverrideType<
  NonNullableFields<components['schemas']['TripView']>,
  {
    legs: TripLegView[];
  }
>;

export type AircraftAvailabilityView = OverrideType<
  NonNullableFields<components['schemas']['AircraftAvailabilityView']>,
  {
    baseAirport: AirportView;
    currentAirport: AirportView;
    availabilities: CrewDutyAvailabilityView[];
    predictedAvailabilities: CrewDutyAvailabilityView[];
    trips: TripView[];
    reservations: ReservationData[];
  }
>;

export type AircraftView = components['schemas']['AircraftView'];

export type BannedTailNumber = OverrideType<
  components['schemas']['BannedTailNumber'],
  {
    id: string;
    tailNumber: string;
    logisticProvider?: LogisticProvider; // Client-only field
  }
>;

export type BannedTailNumberFormType = {
  tailNumber: string;
  logisticProviderId: Uuid | null;
  logisticProviderName?: string;
  comment?: string;
};

export type DriverListFilters = {
  isBanned?: boolean;
  phone?: string;
  name?: string;
  affiliationTypes?: DriverAffiliationType[];
};

export type PaginatedDriverListFilters = PaginationFilterType<DriverListFilters, DriversSearchOrderBy>;

export type Driver = OverrideType<
  components['schemas']['Driver'],
  {
    firstName: string;
    lastName: string;
    txCenterProviderName?: string; // Client-only field
    provider?: LogisticProvider; // Client-only field
  }
>;

export type DriverFormType = {
  firstName: string;
  lastName: string;
  affiliationType: DriverAffiliationType | null;
  txCenterProviderId: Uuid | null;
  txCenterProviderName?: string;
  companyName: string | null;
  phone?: string;
  isBanned: boolean;
  comment?: string;
};

export type CreateOcsConsoleRequestBody = OcsConsoleFormType;
export type PatchOcsConsoleRequestBody = Partial<OcsConsoleFormType>;

export type CreateBannedTailNumberRequestBody = BannedTailNumberFormType;
export type PatchBannedTailNumberRequestBody = Partial<BannedTailNumberFormType>;

export type CreateDriverRequestBody = DriverFormType;
export type PatchDriverRequestBody = Partial<DriverFormType>;

export type ResourcesAvailabilityParams = {
  from?: string;
  to?: string;
  reserved: boolean;
  aircraftIds?: string;
  isContractor?: boolean;
  surgeonAffiliationType?: string;
  exludeShiftAvailability?: boolean;
  employeeId?: string;
};

export type AssignmentCandidateParams = {
  caseId: Uuid;
  orTime?: string;
  organ: OrganType;
  donor: DonorType;
  trialProgramId?: Uuid;
};

export type ReliefAssignmentCSCandidateParams = {
  caseId: Uuid;
  goTime: string;
  organ: OrganType;
  donor: DonorType;
  hubId?: Uuid;
  trialProgramId?: Uuid;
};

export type AssignmentCSCandidatesParams = AssignmentCandidateParams & {
  hubId?: Uuid;
};

export type ResourcesAvailabilityData = {
  aircrafts: AircraftAvailabilityView[];
  employees: EmployeeAvailabilityData[];
  consoles: OcsConsoleData[];
};

export type ReservationNode = OverrideType<
  components['schemas']['Node'],
  {
    caseId: string;
  }
>;

export type EmployeesReservationsParams = {
  from: Date;
  to: Date;
  employeeRoles: EmployeeRole[];
};

export type EmployeesReservationsData = {
  employees: Record<string, ReservationNode[]> | null;
};

export type EmployeeRestPeriodModifyRequestBody = components['schemas']['ModifyEmployeeRestPeriodCommand'];

export type OrganLastCallDays = Record<OrganType, number | undefined>;

export type LastCallInfo = {
  byOrgan: OrganLastCallDays;
  byDonorAndOrgan: Record<DonorType, OrganLastCallDays>;
  forCaseDonorByOrgan: OrganLastCallDays;
  forCaseDonorAndOrgan: number;
};

export type ConflictedReservation = NonNullableFields<components['schemas']['ConflictedReservation']>;

export type ConflictedRest = NonNullableFields<components['schemas']['ConflictedRest']>;

export type EmployeeAssignmentCandidateMatch =
  | components['schemas']['CSAssignmentCandidateMatch']
  | components['schemas']['RSAssignmentCandidateMatch'];

export type RSDays30Stats = components['schemas']['RSDays30Stats'];

export type AssignmentCandidateLocation = NonNullableFields<components['schemas']['AssignmentCandidateLocation']>;

export type RSAffiliation = OverrideType<
  NonNullableFields<components['schemas']['RSAffiliation']>,
  {
    // TODO: Should be replaced by API Location
    entityLocation: Location | null;
  }
>;

export type BaseAssignmentCandidate = {
  employeeId: Uuid;
  employeeProfilePhoto: string;
  employeeFullName: string;
  employeeRoles: EmployeeRole[];
  employeeCertificates: OrganType[];
  lastTrackedLocation?: TrackedLocation;
  baseLocation: Uuid;
  lastCallDays: LastCallInfo;
  conflictedReservation?: ConflictedReservation;
  days30Stats: RSDays30Stats;
};

export type LastCertificationAtHubInfo = {
  lastCertificationAtHubStatus: LastCertificationAtHubStatusType;
  statusChangeAt: string | null;
};

export type LastCertificationsAtHubInfo = Record<Lowercase<OrganType>, LastCertificationAtHubInfo | null>;

export type CSCandidateMatchType = components['schemas']['CSAssignmentCandidateMatch'];

export type CSAssignmentCandidate = OverrideType<
  components['schemas']['CSAssignmentCandidate'],
  BaseAssignmentCandidate & {
    shiftCertificates: OrganType[];
    employeeTrialCertificates: OrganType[];
    employeePhoneSupportOrganCertificates: OrganType[];
    employeeShadowOrganCertificates: OrganType[];
    employeeProctorOrganCertificates: OrganType[];
    baseLocation: Uuid;
    shiftHubId: Uuid;
    conflictedRest?: ConflictedRest;
    lastCertificationAtHubInfo?: LastCertificationsAtHubInfo;
    shiftTimeGroup?: CSShiftTimeGroup | null;
  }
>;

export type RSAssignmentCandidate = OverrideType<
  components['schemas']['RSAssignmentCandidate'],
  BaseAssignmentCandidate & {
    conflictedRest?: ConflictedRest;
    shiftCertificates: OrganType[];
    homeLocation?: AssignmentCandidateLocation;
    unavailabilities: Unavailability[];
  }
>;

export type RSContractorAssignmentCandidate = OverrideType<
  components['schemas']['RSContractorAssignmentCandidate'],
  BaseAssignmentCandidate & {
    affiliation: RSAffiliation;
    homeLocation?: AssignmentCandidateLocation;
  }
>;

export type CCAssignmentCandidate = components['schemas']['CCAssignmentCandidate'];

export type OMSAssignmentCandidate = {
  employeeId: string;
  employeeProfilePhoto: null | string;
  employeeFullName: null | string;
  phone: null | string;
  isAssigned: boolean;
  currentShiftEnd: string;
  currentSlotShiftType: OMSShiftType;
  nextOnCallStart: null | string;
  overlapCSOnCallShiftStart: null | string;
  overlapCSOnCallShiftEnd: null | string;
  overlapPhoneSupportShiftStart: null | string;
  overlapPhoneSupportShiftEnd: null | string;
};

export type CCAssignmentCandidateShift = components['schemas']['CCAssignmentCandidateShift'];

export type CLAssignmentCandidate = OverrideType<
  components['schemas']['CLAssignmentCandidate'],
  {
    csOrganCertificates: OrganType[] | null;
    phoneSupportOrganCertificates: OrganType[] | null;
  }
>;

export type OcsConsoleByIdParams = {
  withDeleted?: boolean;
};
